import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { prescriptionsService } from '@/services/prescriptions';
import { reportsService } from '@/services/reports';
import { patientIdentifier } from '@/lib/patient';
import { PageHeader, KpiCard, StatusBadge, statusDotClass, EmptyState, LoadingState } from '@/components/ui';
import {
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  FilePlus,
  Users,
  ChevronRight,
  Plus,
} from 'lucide-react';

export function DoctorDashboard() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';

  const now = new Date();
  const fromDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const toDate = now.toISOString().split('T')[0]!;
  const allTimeFromDate = '2020-01-01';

  const { data: overview } = useQuery({
    queryKey: ['doctor-overview', fromDate, toDate],
    queryFn: () => reportsService.getDoctorOverview({ fromDate, toDate }),
  });

  const { data: allTimeOverview } = useQuery({
    queryKey: ['doctor-overview', 'all-time', toDate],
    queryFn: () => reportsService.getDoctorOverview({ fromDate: allTimeFromDate, toDate }),
  });

  const { data: recentPrescriptions, isLoading: recentLoading } = useQuery({
    queryKey: ['prescriptions', 'recent'],
    queryFn: () => prescriptionsService.list({ limit: 8 }),
  });

  const allTimeHint = (count?: number) =>
    allTimeOverview && count != null ? t('reports.allTimeTotal', { count }) : undefined;

  const recent = recentPrescriptions?.data ?? [];

  return (
    <div>
      <PageHeader
        title={t('nav.dashboard')}
        description={t('doctor.dashboard.description')}
        actions={
          <Link to="/doctor/prescriptions/new" className="btn-primary">
            <Plus className="h-4 w-4" />
            {t('nav.newPrescription')}
          </Link>
        }
      />

      {/* Stats */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label={t('doctor.dashboard.prescriptionsThisMonth')}
          value={overview?.totalPrescriptions ?? '--'}
          hint={allTimeHint(allTimeOverview?.totalPrescriptions)}
          icon={FileText}
          tone="primary"
          to="/doctor/reports"
        />
        <KpiCard
          label={t('doctor.dashboard.active')}
          value={overview?.activePrescriptions ?? '--'}
          hint={allTimeHint(allTimeOverview?.activePrescriptions)}
          icon={Clock}
          tone="navy"
          to="/doctor/reports"
        />
        <KpiCard
          label={t('doctor.dashboard.fulfilled')}
          value={overview?.fulfilledPrescriptions ?? '--'}
          hint={allTimeHint(allTimeOverview?.fulfilledPrescriptions)}
          icon={CheckCircle2}
          tone="success"
          to="/doctor/reports"
        />
        <KpiCard
          label={t('doctor.dashboard.expired')}
          title={t('doctor.dashboard.expiredHint')}
          value={overview?.expiredPrescriptions ?? '--'}
          hint={allTimeHint(allTimeOverview?.expiredPrescriptions)}
          icon={AlertTriangle}
          tone="warning"
          to="/doctor/reports"
        />
      </div>

      {/* Quick actions */}
      <div className="mb-8 grid gap-4 md:grid-cols-2">
        <Link
          to="/doctor/prescriptions/new"
          className="card card-interactive group flex items-center gap-4 p-5"
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white">
            <FilePlus className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="card-title">{t('nav.newPrescription')}</h2>
            <p className="mt-0.5 text-sm text-gray-500">{t('doctor.dashboard.newPrescriptionHint')}</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 rtl-flip" />
        </Link>

        <Link
          to="/doctor/patients"
          className="card card-interactive group flex items-center gap-4 p-5"
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-navy-50 text-navy-700">
            <Users className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="card-title">{t('nav.patients')}</h2>
            <p className="mt-0.5 text-sm text-gray-500">{t('doctor.dashboard.patientsHint')}</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 rtl-flip" />
        </Link>
      </div>

      {/* Recent prescriptions timeline */}
      <div className="card p-0">
        <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <h2 className="card-title">{t('doctor.dashboard.recentActivity')}</h2>
          <Link to="/doctor/prescriptions" className="link inline-flex items-center gap-1 text-sm">
            {t('common.seeAll')}
            <ChevronRight className="h-4 w-4 rtl-flip" />
          </Link>
        </div>

        {recentLoading ? (
          <LoadingState className="py-12" />
        ) : recent.length > 0 ? (
          <div className="relative px-5 py-4">
            {/* Timeline line */}
            <div className="absolute bottom-6 start-[28px] top-6 w-0.5 bg-gray-200" />

            <div className="space-y-3">
              {recent.map((rx) => (
                <Link
                  key={rx.id}
                  to={`/doctor/prescriptions/${rx.id}`}
                  className="group relative flex items-start gap-4 ps-7"
                >
                  {/* Timeline dot */}
                  <div
                    className={`absolute start-0 top-3.5 h-[18px] w-[18px] rounded-full border-[3px] border-white shadow-sm ${statusDotClass(rx.status)}`}
                  />

                  <div className="min-w-0 flex-1 rounded-lg border border-transparent bg-gray-50 p-3 transition-colors group-hover:border-gray-200 group-hover:bg-white">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <p className="truncate font-medium text-gray-900">
                        {t('doctor.rxRef', { id: rx.id.slice(-8) })}
                      </p>
                      <StatusBadge status={rx.status} />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm text-gray-500">
                      <span>
                        {t('doctor.patientLabel')}: {rx.patient ? patientIdentifier(rx.patient).masked : '—'}
                      </span>
                      <span className="tabular-nums">
                        {new Date(rx.createdAt).toLocaleDateString(dateLocale)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={FileText}
            title={t('doctor.dashboard.noRecent')}
            description={t('doctor.dashboard.noRecentHint')}
            action={
              <Link to="/doctor/prescriptions/new" className="btn-primary btn-sm">
                <Plus className="h-4 w-4" />
                {t('nav.newPrescription')}
              </Link>
            }
          />
        )}
      </div>
    </div>
  );
}
